import { useState } from 'react';
import { api } from '../api';
import { unit, useLoad } from '../lib';

// Smart Reorder — per-camp cadence suggestions ("this camp orders this every ~N days; the next one
// looks due"). Assisted reminders only; nothing here places an order. Compute recomputes one org now.
const STATUS_LABEL: Record<string, string> = { overdue: 'Overdue', due: 'Due', due_soon: 'Due soon', stale: 'Still need?' };

export function ReorderPanel() {
  const [orgId, setOrgId] = useState('442');
  const [applied, setApplied] = useState('442');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const validOrg = Number.isInteger(Number(applied)) && Number(applied) > 0;
  const { data, err, reload } = useLoad(
    () => (validOrg ? api.commerceReorder(Number(applied), status) : Promise.resolve([])),
    [applied, status],
  );

  async function compute() {
    const id = Number(orgId);
    if (!Number.isInteger(id) || id <= 0) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.commerceReorderCompute(id);
      setMsg(`✓ Computed ${r.inserted.toLocaleString()} suggestions for org ${id} (as of ${r.asOf}, ${r.ms}ms)`);
      if (applied === orgId) reload();
      else setApplied(orgId);
    } catch (e) {
      setMsg(`⚠ ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <h1>Reorder</h1>
      <p className="sub">Cadence-based reorder suggestions per camp — assisted reminders, never auto-orders. Recomputed daily; Compute refreshes one org now.</p>
      <div className="toolbar">
        <label className="inline-field">Org ID
          <input className="search short" value={orgId} onChange={(e) => setOrgId(e.target.value.replace(/\D/g, ''))} onKeyDown={(e) => e.key === 'Enter' && setApplied(orgId)} />
        </label>
        <select className="sel" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="overdue">Overdue</option>
          <option value="due">Due</option>
          <option value="due_soon">Due soon</option>
          <option value="stale">Still need? (stale)</option>
        </select>
        <button className="btn-sm" onClick={() => setApplied(orgId)}>Load</button>
        <button className="btn-sm ghost" disabled={busy} onClick={compute}>{busy ? 'Computing…' : 'Compute now'}</button>
      </div>
      {err && <p className="msg bad">⚠ {err}</p>}
      {msg && <p className={`msg ${msg.startsWith('⚠') ? 'bad' : 'good'}`}>{msg}</p>}
      <section className="card">
        <table>
          <thead><tr><th>Item</th><th>Last delivery</th><th className="num">Every ~</th><th>Next expected</th><th>Status</th><th className="num">Obs</th><th className="num">Best unit price today</th></tr></thead>
          <tbody>
            {(data ?? []).map((r) => (
              <tr key={r.item_id}>
                <td><span style={{ fontWeight: 600 }}>{r.name}</span><div className="muted mono" style={{ fontSize: 'var(--fs-small)' }}>{r.sku}</div></td>
                <td className="mono">{r.last_delivery}</td>
                <td className="num mono">{r.avg_interval_days}d{r.stddev_days != null ? ` ± ${r.stddev_days}` : ''}</td>
                <td className="mono">{r.next_expected}</td>
                <td><span className={`reorder-pill ${r.status}`}>{STATUS_LABEL[r.status] ?? r.status}</span></td>
                <td className="num">{r.observations}</td>
                <td className="num">{unit(r.best_unit_price)}{r.best_vendor ? <div className="muted" style={{ fontSize: 'var(--fs-small)' }}>{r.best_vendor}{r.best_org_assigned === false ? ' (not assigned)' : ''}</div> : null}</td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={7} className="loading">No suggestions — run Compute for this org.</td></tr>}
          </tbody>
        </table>
        {!data && !err && <p className="loading">Loading…</p>}
      </section>
    </div>
  );
}
